import { s } from '@/theme/tokens';
import { Box, Text } from '@/ui';
import { Chip } from '@/ui/primitives';
import { resolveThresholdTone, type ThresholdDirection } from './utils';
import type { ReactElement } from 'react';

type ThresholdPreviewChipsProps = {
  values: { red: number; yellowMin: number; yellowMax: number; green: number };
  formatter: (value: number) => string;
  direction: ThresholdDirection;
  warn: number;
  target: number;
  disabled?: boolean;
};

export const ThresholdPreviewChips = ({
  values,
  formatter,
  direction,
  warn,
  target,
  disabled = false,
}: ThresholdPreviewChipsProps): ReactElement => {
  const thresholds = { warn, target, direction } as const;
  const basePreviews = [
    { key: 'red', value: values.red },
    { key: 'yellow-min', value: values.yellowMin },
    { key: 'yellow-max', value: values.yellowMax },
    { key: 'green', value: values.green },
  ] as const;
  const previews =
    direction === 'lowerBetter'
      ? [basePreviews[0], basePreviews[2], basePreviews[1], basePreviews[3]]
      : basePreviews;
  const labelByKey: Record<(typeof basePreviews)[number]['key'], string> = {
    red: 'Alert max.',
    'yellow-min': 'Warn min.',
    'yellow-max': 'Warn max.',
    green: 'Target min.',
  };

  return (
    <Box sx={{ display: 'grid', gap: s('sm') }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: s('sm') }}>
        {previews.map((preview) => (
          <Text
            key={`${preview.key}-label`}
            role="body"
            color="textSecondary"
            sx={{ textAlign: 'center' }}
          >
            {labelByKey[preview.key]}
          </Text>
        ))}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: s('sm') }}>
        {previews.map((preview) => (
          <Box key={preview.key} sx={{ display: 'flex', justifyContent: 'center' }}>
            <Chip
              label={formatter(preview.value)}
              tone={disabled ? 'default' : resolveThresholdTone(preview.value, thresholds)}
              sx={[
                {
                  justifyContent: 'center',
                  '& .MuiChip-label': {
                    textAlign: 'center',
                  },
                },
                ...(disabled
                  ? [
                      {
                        backgroundColor: 'transparent',
                        border: 'none',
                        color: 'text.secondary',
                        '& .MuiChip-label': {
                          color: 'text.secondary',
                          textAlign: 'center',
                        },
                      },
                    ]
                  : []),
              ]}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};
